import {
	ChangeDetectionStrategy,
	Component,
	effect,
	inject,
	input,
	InputSignal,
	output,
	signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
	CentsToDollarsPipe,
	PercentToValuePipe,
} from '@marmon/frontend/common';
import { Analysis, AnalysisCalculation } from '@marmon/shared/types/analysis';
import { QueryConstraintMap } from '@marmon/shared/types/common';
import { AnalysisApiService } from '../../services';
import { BreakdownCardButtonComponent } from './breakdown-card-button/breakdown-card-button.component';

export type BreakdownSectionOption = {
	label: string;
	appearance: 'green' | 'red';
	queryConstraints: QueryConstraintMap[];
	key: string;
};

export type BreakdownSection = {
	label?: string;
	sublabel?: string;
	key: string;
	value?: number;
	options: BreakdownSectionOption[];
	valueType?: 'percent' | 'dollars';
};

export type BreakdownSectionGroup = {
	header: string;
	key: string;
	sections: BreakdownSection[];
	value?: number;
	valueType?: 'percent' | 'dollars';
};

@Component({
	imports: [
		TranslocoPipe,
		BreakdownCardButtonComponent,
		CentsToDollarsPipe,
		PercentToValuePipe,
	],
	selector: 'mar-breakdown-card',
	templateUrl: 'breakdown-card.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'contents' },
})
export class BreakdownCardComponent {
	readonly header = input.required<string>();
	readonly active = input<string | null>();
	readonly layout = input<'tight' | 'normal'>('normal');

	readonly analysisApiService = inject(AnalysisApiService);

	readonly analysis = input.required<Analysis>();
	readonly calculation: InputSignal<AnalysisCalculation> =
		input.required<AnalysisCalculation>();
	readonly entity = input.required<'customers' | 'products'>();

	readonly sections = input.required<BreakdownSection[]>();

	readonly optionCount = signal<number>(0);
	readonly loadCount = signal<number>(0);

	readonly marSelect = output<BreakdownSectionOption>();
	readonly marLoaded = output<void>();

	constructor() {
		effect(() => {
			this.loadCount.set(0);

			let optionCount = 0;
			this.sections().forEach((section) => {
				optionCount += section.options.length;
			});

			this.optionCount.set(optionCount);
		});
	}

	onSelect(value: BreakdownSectionOption) {
		this.marSelect.emit(value);
	}

	onLoaded() {
		this.loadCount.set(this.loadCount() + 1);
		if (this.loadCount() < this.optionCount()) return;
		// if we have loaded all of the options then we can emit that we are done loading
		this.marLoaded.emit();
	}
}
